import { DismissPromptClientsVariables, DismissablePromptsClientsVariables } from '@ms/yammer-graphql';
import mutationDismissPrompt from '@ms/yammer-graphql/dist/operations/mutationDismissPrompt.graphql';
import queryDismissablePrompts from '@ms/yammer-graphql/dist/operations/queryDismissablePrompts.graphql';

import { GraphqlApiRequest, ManagedApiLifecycle, getGraphqlApiRequest, managedApiAction } from '../..';
import { normalizeDismissablePromptsResponse } from '../../normalization/graphql/normalizeDismissablePrompt';
import { DismissPromptResponse, DismissablePromptsResponse } from '../../normalization/types';
import { DismissablePrompt, DismissablePromptKey } from '../../state/types';
import { reportError } from '../../telemetry';
import { PromiseThunkAction } from '../../types';

import {
  DismissPromptFulfilledAction,
  DismissPromptPendingAction,
  DismissPromptRejectedAction,
  LoadDismissablePromptsFulfilledAction,
  LoadDismissablePromptsPendingAction,
  LoadDismissablePromptsRejectedAction,
} from './actions.types';

const dismissPromptPending = (promptKey: DismissablePromptKey): DismissPromptPendingAction => ({
  type: 'DISMISSABLE_PROMPTS.DISMISS_PROMPT_PENDING',
  payload: {
    promptKey,
  },
});

const dismissPromptFulfilled = (promptKey: DismissablePromptKey): DismissPromptFulfilledAction => ({
  type: 'DISMISSABLE_PROMPTS.DISMISS_PROMPT_FULFILLED',
  payload: {
    promptKey,
  },
});

const dismissPromptRejected = (error: unknown, promptKey: DismissablePromptKey): DismissPromptRejectedAction => ({
  type: 'DISMISSABLE_PROMPTS.DISMISS_PROMPT_REJECTED',
  payload: {
    error,
    promptKey,
  },
  error: true,
});

const updateDismissablePromptRequest = (
  promptKey: DismissablePromptKey
): GraphqlApiRequest<DismissPromptClientsVariables> =>
  getGraphqlApiRequest({
    body: {
      operationName: 'DismissPromptClients',
      query: mutationDismissPrompt,
      variables: {
        promptKey,
      },
    },
  });

const updateDismissablePromptLifecycle = (
  promptKey: DismissablePromptKey
): ManagedApiLifecycle<DismissPromptResponse> => ({
  pending: () => dismissPromptPending(promptKey),
  fulfilled: () => dismissPromptFulfilled(promptKey),
  rejected: (error) => dismissPromptRejected(error, promptKey),
});

export type UpdateDismissablePrompt = (promptKey: DismissablePromptKey) => PromiseThunkAction<void>;
export const updateDismissablePrompt: UpdateDismissablePrompt = (promptKey) => async (dispatch) => {
  const request = updateDismissablePromptRequest(promptKey);
  const lifecycle = updateDismissablePromptLifecycle(promptKey);
  const action = managedApiAction({ request, lifecycle });

  try {
    await dispatch(action);
  } catch (error) {
    reportError({ error });
  }
};

const loadDismissablePromptsPending = (promptKeys: DismissablePromptKey[]): LoadDismissablePromptsPendingAction => ({
  type: 'DISMISSABLE_PROMPTS.LOAD_PENDING',
  payload: {
    promptKeys,
  },
});

const loadDismissablePromptsFulfilled = (
  entities: Record<string, DismissablePrompt>
): LoadDismissablePromptsFulfilledAction => ({
  type: 'DISMISSABLE_PROMPTS.LOAD_FULFILLED',
  payload: {
    entities,
  },
});

const loadDismissablePromptsRejected = (
  promptKeys: DismissablePromptKey[],
  error: unknown
): LoadDismissablePromptsRejectedAction => ({
  type: 'DISMISSABLE_PROMPTS.LOAD_REJECTED',
  payload: {
    promptKeys,
    error,
  },
  error: true,
});

const loadDismissablePromptsRequest = (
  promptKeys: DismissablePromptKey[]
): GraphqlApiRequest<DismissablePromptsClientsVariables> =>
  getGraphqlApiRequest({
    body: {
      operationName: 'DismissablePromptsClients',
      query: queryDismissablePrompts,
      variables: {
        promptKeys,
      },
    },
  });

const loadDismissablePromptsLifecycle = (
  promptKeys: DismissablePromptKey[]
): ManagedApiLifecycle<DismissablePromptsResponse> => ({
  pending: () => loadDismissablePromptsPending(promptKeys),
  fulfilled: (response) => loadDismissablePromptsFulfilled(normalizeDismissablePromptsResponse(response)),
  rejected: (error) => loadDismissablePromptsRejected(promptKeys, error),
});

export type LoadDismissablePrompts = (promptKeys: DismissablePromptKey[]) => PromiseThunkAction<void>;
export const loadDismissablePrompts: LoadDismissablePrompts = (promptKeys) => async (dispatch) => {
  const request = loadDismissablePromptsRequest(promptKeys);
  const lifecycle = loadDismissablePromptsLifecycle(promptKeys);
  const action = managedApiAction({ request, lifecycle });

  try {
    await dispatch(action);
  } catch (error) {
    reportError({ error });
  }
};
